'use client';

import React from 'react';
import { cn } from 'utils/styles';
import ChimeKitIcon from 'components/shared/LinkWithIcon/ChimeKitIcon';
import { VideoControlsProps, VideoPlayerState } from '../../types';

const VideoControls: React.FC<VideoControlsProps> = ({
  position = 'bottom-right',
  className,
  hideControls = false,
  showOnVideoEnd = false,
  hideOnVideoStarted = true,
  videoPlayerState,
}) => {
  if (!videoPlayerState) {
    return null;
  }

  const { playerState, isPlaying, handlePlayClick, handlePauseClick, testId } = videoPlayerState;

  // Calculate whether controls should be shown
  const shouldShowControls = (() => {
    // If hideControls is true, always hide (takes precedence)
    if (hideControls) {
      return false;
    }

    // Show controls on end if showOnVideoEnd is true (this overrides hideOnVideoStarted)
    if (playerState === VideoPlayerState.Ended && showOnVideoEnd) {
      return true;
    }

    // Hide controls after video has started if hideOnVideoStarted is true
    // This means hide for Playing and Ended states
    const validStatesForControls = [VideoPlayerState.Init, VideoPlayerState.Starting, VideoPlayerState.Paused];
    const isVideoStarted = !validStatesForControls.includes(playerState);
    if (hideOnVideoStarted && isVideoStarted) {
      return false;
    }

    // Show controls in all other cases
    return true;
  })();

  if (!shouldShowControls) {
    return null;
  }

  const positionClasses = {
    'bottom-right': 'absolute bottom-6 right-6',
    center: 'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
  };

  const baseClasses = cn(
    'z-10 flex h-12 w-12 items-center justify-center rounded-full shadow-lg transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white/50 text-content-inverse cursor-pointer',
    positionClasses[position],
    className,
  );

  const PlayButton = <ChimeKitIcon iconName="PlayCircle" width={48} height={48} testId={testId} />;
  const PauseButton = <ChimeKitIcon iconName="PauseCircle" width={48} height={48} testId={testId} />;

  return (
    <button
      onClick={isPlaying ? handlePauseClick : handlePlayClick}
      className={baseClasses}
      data-testid={`${testId}-play-pause-button`}
      aria-label={isPlaying ? 'Pause video' : 'Play video'}>
      {isPlaying ? PauseButton : PlayButton}
    </button>
  );
};

export default VideoControls;
